import { Brand } from './utils/Brand';
import { zodBrandedString, zodBrandedUuid } from './utils/Zod';

const NonStandardCaseBrandedIdKeys = ['MERSMIN', 'MERSOrgId'] as const;
export type NonStandardCaseBrandedIdKey = (typeof NonStandardCaseBrandedIdKeys)[number];

export const BrandedIdTypes = [
  'AchBatchId',
  'AchBatchScheduleId',
  'AchBatchTransactionId',
  'AdvanceId',
  'ApiKeyId',
  'AppraisalId',
  'AuditLogId',
  'AuthorizedContactId',
  'AuthZeroId',
  'BalanceHistoryItemId',
  'BankruptcyId',
  'BankruptcyProofOfClaimId',
  'BannerMessageId',
  'BillSettingsId',
  'BorrowerAchAccountId',
  'BorrowerId',
  'CascadeUpdateId',
  'ChatQueryId',
  'CompanyAchAccountId',
  'CompanyAuditLogId',
  'CompanyId',
  'CompanyInterestOnEscrowSettingId',
  'CompanyReportDownloadId',
  'CompanyReportId',
  'CompanyReportQueryId',
  'CompanyReportQueryRevisionId',
  'CompanyWireSettingsId',
  'CustodialAccountId',
  'CustodialAccountBalanceId',
  'CustomFAQItemId',
  'CustomFAQSectionId',
  'CustomTemplateId',
  'CustomCompanyFieldId',
  'DelinquentInterestSettingId',
  'DisbursementId',
  'DocumentId',
  'DrawId',
  'EntityId',
  'EscrowCompanyId',
  'EscrowDueId',
  'EscrowPaymentHistoryItemId',
  'EscrowSettingId',
  'EventId',
  'ExternalEntityId',
  'ExternalLoanIdRecordId',
  'ExternalLoanIdValue',
  'FeeId',
  'FeeLineItemId',
  'ForeclosureId',
  'HistoricalTransactionId',
  'IndexRateId',
  'InspectionId',
  'InterestOnEscrowDepositHistoryItemId',
  'InvestorId',
  'InvestorReportingId',
  'LedgerPostingId',
  'LedgerTransactionId',
  'LiquidationId',
  'LoanChatMessageId',
  'LoanCreditReportId',
  'LoanId',
  'LoanImportId',
  'LoanImportRowId',
  'LoanInvestorRemittanceSplitId',
  'LoanNoteId',
  'LoanOwnerId',
  'LoanTransferExportId',
  'LoanWorkflowId',
  'LosId',
  'MaturityHistoryItemId',
  'NonPerformanceCodeForReportingCycleId',
  'NoteflowCompanyId',
  'PayeeId',
  'PaymentAccountId',
  'PaymentBreakdownId',
  'PaymentConfigId',
  'PaymentDueId',
  'PaymentId',
  'PayoffId',
  'PoolId',
  'PoolInternalId',
  'PortalUserId',
  'PrincipalModificationId',
  'PropertyId',
  'QcDocumentId',
  'QrpcId',
  'RecurringCompanyReportId',
  'RepayAccountId',
  'ReportableEventId',
  'SftpKeyId',
  'SftpSiteId',
  'SideEffectId',
  'StepActivationId',
  'StepDefinitionId',
  'StepInstanceId',
  'StepNoteId',
  'TaskAuditLogId',
  'TaskId',
  'TeamId',
  'TeamPermissionsId',
  'TeamSpecialistRoleId',
  'TeamTasksId',
  'TransactionId',
  'TransferId',
  'UserId',
  'UsioAccountId',
  'UsioCompanyId',
  'WorkflowDefinitionId',
  'WorkflowInstanceId',
  'WorkoutPlanId',
  'WriteoffId',
  'FileImportId',
  'ColumnMappingId',
  'ColumnEnumMappingId',
  ...NonStandardCaseBrandedIdKeys,
] as const;

export type BrandedIdKey = (typeof BrandedIdTypes)[number];
export type BrandedId<T extends BrandedIdKey> = Brand<string, T>;

export type AllBrandedIds = BrandedId<BrandedIdKey>;

export type AchBatchId = BrandedId<'AchBatchId'>;
export const AchBatchId = zodBrandedUuid<AchBatchId>();
export type AchBatchScheduleId = BrandedId<'AchBatchScheduleId'>;
export const AchBatchScheduleId = zodBrandedUuid<AchBatchScheduleId>();
export type AchBatchTransactionId = BrandedId<'AchBatchTransactionId'>;
export const AchBatchTransactionId = zodBrandedUuid<AchBatchTransactionId>();
export type ApiKeyId = BrandedId<'ApiKeyId'>;
export const ApiKeyId = zodBrandedUuid<ApiKeyId>();
export type AuthorizedContactId = BrandedId<'AuthorizedContactId'>;
export const AuthorizedContactId = zodBrandedUuid<AuthorizedContactId>();
export type AuthZeroId = BrandedId<'AuthZeroId'>;
export const AuthZeroId = zodBrandedUuid<AuthZeroId>();
export type BillSettingsId = BrandedId<'BillSettingsId'>;
export const BillSettingsId = zodBrandedUuid<BillSettingsId>();
export type BorrowerId = BrandedId<'BorrowerId'>;
export const BorrowerId = zodBrandedUuid<BorrowerId>();
export type BorrowerAchAccountId = BrandedId<'BorrowerAchAccountId'>;
export const BorrowerAchAccountId = zodBrandedUuid<BorrowerAchAccountId>();
export type CascadeUpdateId = BrandedId<'CascadeUpdateId'>;
export const CascadeUpdateId = zodBrandedUuid<CascadeUpdateId>();
export type CompanyId = BrandedId<'CompanyId'>;
export const CompanyId = zodBrandedUuid<CompanyId>();
export type CompanyAuditLogId = BrandedId<'CompanyAuditLogId'>;
export const CompanyAuditLogId = zodBrandedUuid<CompanyAuditLogId>();
export type LoanId = BrandedId<'LoanId'>;
export const LoanId = zodBrandedUuid<LoanId>();
export type LosId = BrandedId<'LosId'>;
export const LosId = zodBrandedString<LosId>();
export type PortalUserId = BrandedId<'PortalUserId'>;
export const PortalUserId = zodBrandedUuid<PortalUserId>();
export type UserId = BrandedId<'UserId'>;
export const UserId = zodBrandedUuid<UserId>();
export type PayeeId = BrandedId<'PayeeId'>;
export const PayeeId = zodBrandedUuid<PayeeId>();
export type PaymentId = BrandedId<'PaymentId'>;
export const PaymentId = zodBrandedUuid<PaymentId>();
export type PaymentAccountId = BrandedId<'PaymentAccountId'>;
export const PaymentAccountId = zodBrandedString<PaymentAccountId>();
export type PaymentDueId = BrandedId<'PaymentDueId'>;
export const PaymentDueId = zodBrandedUuid<PaymentDueId>();
export type DisbursementId = BrandedId<'DisbursementId'>;
export const DisbursementId = zodBrandedUuid<DisbursementId>();
export type LoanImportId = BrandedId<'LoanImportId'>;
// LoanImport.id is an opaque string PK ("batch id from flat file"). CSV uploads via
// processFileImport reuse the upstream Flatfile sheet id (e.g. `us_sh_O0zwPvNp`), so
// the runtime validator must accept any non-empty string — not just UUIDs.
export const LoanImportId = zodBrandedString<LoanImportId>();
export type LoanImportRowId = BrandedId<'LoanImportRowId'>;
// Mirrors LoanImportId: rows currently get UUIDs from brandedId(), but the column
// is a free-form String PK and the value flows into FKs that may not be UUIDs.
export const LoanImportRowId = zodBrandedString<LoanImportRowId>();
export type FileImportId = BrandedId<'FileImportId'>;
export const FileImportId = zodBrandedUuid<FileImportId>();
export type HistoricalTransactionId = BrandedId<'HistoricalTransactionId'>;
export const HistoricalTransactionId = zodBrandedUuid<HistoricalTransactionId>();
export type ColumnMappingId = BrandedId<'ColumnMappingId'>;
export const ColumnMappingId = zodBrandedUuid<ColumnMappingId>();
export type ColumnEnumMappingId = BrandedId<'ColumnEnumMappingId'>;
export const ColumnEnumMappingId = zodBrandedUuid<ColumnEnumMappingId>();
export type LoanTransferExportId = BrandedId<'LoanTransferExportId'>;
export type EventId = BrandedId<'EventId'>;
export const EventId = zodBrandedUuid<EventId>();
export type SideEffectId = BrandedId<'SideEffectId'>;
export const SideEffectId = zodBrandedUuid<SideEffectId>();
export type LoanNoteId = BrandedId<'LoanNoteId'>;
export const LoanNoteId = zodBrandedUuid<LoanNoteId>();
export type TransferId = BrandedId<'TransferId'>;
export const TransferId = zodBrandedUuid<TransferId>();
export type FeeId = BrandedId<'FeeId'>;
export const FeeId = zodBrandedUuid<FeeId>();
// Same UUID as the underlying Fee on the snapshot; coerce via
// `asAdvanceId(advance)` in Charges.ts.
export type AdvanceId = BrandedId<'AdvanceId'>;
export const AdvanceId = zodBrandedUuid<AdvanceId>();
export type FeeLineItemId = BrandedId<'FeeLineItemId'>;
export const FeeLineItemId = zodBrandedUuid<FeeLineItemId>();
export type AuditLogId = BrandedId<'AuditLogId'>;
export const AuditLogId = zodBrandedUuid<AuditLogId>();
export type EntityId = BrandedId<'EntityId'>;
export const EntityId = zodBrandedUuid<EntityId>();
export type ExternalEntityId = BrandedId<'ExternalEntityId'>;
export type DrawId = BrandedId<'DrawId'>;
export const DrawId = zodBrandedUuid<DrawId>();
export type PayoffId = BrandedId<'PayoffId'>;
export const PayoffId = zodBrandedUuid<PayoffId>();
export type DocumentId = BrandedId<'DocumentId'>;
export const DocumentId = zodBrandedUuid<DocumentId>();
export type EscrowCompanyId = BrandedId<'EscrowCompanyId'>;
export const EscrowCompanyId = zodBrandedUuid<EscrowCompanyId>();
export type PoolId = BrandedId<'PoolId'>;
export const PoolId = zodBrandedString<PoolId>();
export type PoolInternalId = BrandedId<'PoolInternalId'>;
export const PoolInternalId = zodBrandedUuid<PoolInternalId>();
export type PropertyId = BrandedId<'PropertyId'>;
export const PropertyId = zodBrandedUuid<PropertyId>();
export type TaskId = BrandedId<'TaskId'>;
export const TaskId = zodBrandedUuid<TaskId>();
export type TaskAuditLogId = BrandedId<'TaskAuditLogId'>;
export const TaskAuditLogId = zodBrandedUuid<TaskAuditLogId>();
export type CompanyReportId = BrandedId<'CompanyReportId'>;
export const CompanyReportId = zodBrandedUuid<CompanyReportId>();
export type CompanyReportDownloadId = BrandedId<'CompanyReportDownloadId'>;
export const CompanyReportDownloadId = zodBrandedUuid<CompanyReportDownloadId>();
export type CompanyReportQueryId = BrandedId<'CompanyReportQueryId'>;
export const CompanyReportQueryId = zodBrandedUuid<CompanyReportQueryId>();
export type CompanyReportQueryRevisionId = BrandedId<'CompanyReportQueryRevisionId'>;
export const CompanyReportQueryRevisionId = zodBrandedUuid<CompanyReportQueryRevisionId>();
export type QcDocumentId = BrandedId<'QcDocumentId'>;
export const QcDocumentId = zodBrandedUuid<QcDocumentId>();
export type EscrowSettingId = BrandedId<'EscrowSettingId'>;
export const EscrowSettingId = zodBrandedUuid<EscrowSettingId>();
export type EscrowDueId = BrandedId<'EscrowDueId'>;
export const EscrowDueId = zodBrandedUuid<EscrowDueId>();
export type LoanChatMessageId = BrandedId<'LoanChatMessageId'>;
export type UsioCompanyId = BrandedId<'UsioCompanyId'>;
export type UsioAccountId = BrandedId<'UsioAccountId'>;
export type RepayAccountId = BrandedId<'RepayAccountId'>;
export const RepayAccountId = zodBrandedUuid<RepayAccountId>();
export type RecurringCompanyReportId = BrandedId<'RecurringCompanyReportId'>;
export const RecurringCompanyReportId = zodBrandedUuid<RecurringCompanyReportId>();
export type ChatQueryId = BrandedId<'ChatQueryId'>;
export const ChatQueryId = zodBrandedUuid<ChatQueryId>();
export type EscrowPaymentHistoryItemId = BrandedId<'EscrowPaymentHistoryItemId'>;
export const EscrowPaymentHistoryItemId = zodBrandedUuid<EscrowPaymentHistoryItemId>();
export type LoanInvestorRemittanceSplitId = BrandedId<'LoanInvestorRemittanceSplitId'>;
export const LoanInvestorRemittanceSplitId = zodBrandedUuid<LoanInvestorRemittanceSplitId>();
export type InterestOnEscrowDepositHistoryItemId = BrandedId<'InterestOnEscrowDepositHistoryItemId'>;
export const InterestOnEscrowDepositHistoryItemId = zodBrandedUuid<InterestOnEscrowDepositHistoryItemId>();
export type IndexRateId = BrandedId<'IndexRateId'>;
export const IndexRateId = zodBrandedUuid<IndexRateId>();
export type PaymentConfigId = BrandedId<'PaymentConfigId'>;
export const PaymentConfigId = zodBrandedUuid<PaymentConfigId>();
export type CustomFAQSectionId = BrandedId<'CustomFAQSectionId'>;
export const CustomFAQSectionId = zodBrandedUuid<CustomFAQSectionId>();
export type CustomFAQItemId = BrandedId<'CustomFAQItemId'>;
export const CustomFAQItemId = zodBrandedUuid<CustomFAQItemId>();
export type CustomTemplateId = BrandedId<'CustomTemplateId'>;
export const CustomTemplateId = zodBrandedUuid<CustomTemplateId>();
export type CustomCompanyFieldId = BrandedId<'CustomCompanyFieldId'>;
export const CustomCompanyFieldId = zodBrandedUuid<CustomCompanyFieldId>();
export type CustodialAccountId = BrandedId<'CustodialAccountId'>;
export const CustodialAccountId = zodBrandedUuid<CustodialAccountId>();
export type CustodialAccountBalanceId = BrandedId<'CustodialAccountBalanceId'>;
export const CustodialAccountBalanceId = zodBrandedUuid<CustodialAccountBalanceId>();
export type CompanyAchAccountId = BrandedId<'CompanyAchAccountId'>;
export const CompanyAchAccountId = zodBrandedUuid<CompanyAchAccountId>();
export type CompanyInterestOnEscrowSettingId = BrandedId<'CompanyInterestOnEscrowSettingId'>;
export const CompanyInterestOnEscrowSettingId = zodBrandedString<CompanyInterestOnEscrowSettingId>();
export type CompanyWireSettingsId = BrandedId<'CompanyWireSettingsId'>;
export const CompanyWireSettingsId = zodBrandedUuid<CompanyWireSettingsId>();
export type WorkoutPlanId = BrandedId<'WorkoutPlanId'>;
export const WorkoutPlanId = zodBrandedUuid<WorkoutPlanId>();
export type LoanWorkflowId = BrandedId<'LoanWorkflowId'>;
export const LoanWorkflowId = zodBrandedUuid<LoanWorkflowId>();
export type InvestorId = BrandedId<'InvestorId'>;
export type LoanOwnerId = BrandedId<'LoanOwnerId'>;
export const LoanOwnerId = zodBrandedString<LoanOwnerId>();
export type MaturityHistoryItemId = BrandedId<'MaturityHistoryItemId'>;
export const MaturityHistoryItemId = zodBrandedUuid<MaturityHistoryItemId>();
export type SftpSiteId = BrandedId<'SftpSiteId'>;
export const SftpSiteId = zodBrandedUuid<SftpSiteId>();
export type SftpKeyId = BrandedId<'SftpKeyId'>;
export const SftpKeyId = zodBrandedUuid<SftpKeyId>();
export type ExternalLoanIdRecordId = BrandedId<'ExternalLoanIdRecordId'>;
export const ExternalLoanIdRecordId = zodBrandedUuid<ExternalLoanIdRecordId>();
export type ExternalLoanIdValue = BrandedId<'ExternalLoanIdValue'>;
export const ExternalLoanIdValue = zodBrandedString<ExternalLoanIdValue>();
export type LoanCreditReportId = BrandedId<'LoanCreditReportId'>;
export const LoanCreditReportId = zodBrandedUuid<LoanCreditReportId>();
export type AppraisalId = BrandedId<'AppraisalId'>;
export const AppraisalId = zodBrandedUuid<AppraisalId>();
export type InspectionId = BrandedId<'InspectionId'>;
export const InspectionId = zodBrandedUuid<InspectionId>();
export type NoteflowCompanyId = BrandedId<'NoteflowCompanyId'>;
export type PrincipalModificationId = BrandedId<'PrincipalModificationId'>;
export const PrincipalModificationId = zodBrandedUuid<PrincipalModificationId>();
export type WriteoffId = BrandedId<'WriteoffId'>;
export const WriteoffId = zodBrandedUuid<WriteoffId>();
export type InvestorReportingId = BrandedId<'InvestorReportingId'>;
export const InvestorReportingId = zodBrandedUuid<InvestorReportingId>();
export type TeamId = BrandedId<'TeamId'>;
export const TeamId = zodBrandedUuid<TeamId>();
export type TeamPermissionsId = BrandedId<'TeamPermissionsId'>;
export const TeamPermissionsId = zodBrandedUuid<TeamPermissionsId>();
export type TeamTasksId = BrandedId<'TeamTasksId'>;
export const TeamTasksId = zodBrandedUuid<TeamTasksId>();
export type TeamSpecialistRoleId = BrandedId<'TeamSpecialistRoleId'>;
export const TeamSpecialistRoleId = zodBrandedUuid<TeamSpecialistRoleId>();
export type MERSMIN = BrandedId<'MERSMIN'>;
export const MERSMIN = zodBrandedString<MERSMIN>();
export type MERSOrgId = BrandedId<'MERSOrgId'>;
export const MERSOrgId = zodBrandedString<MERSOrgId>();
export type WorkflowDefinitionId = BrandedId<'WorkflowDefinitionId'>;
export const WorkflowDefinitionId = zodBrandedString<WorkflowDefinitionId>();
export type WorkflowInstanceId = BrandedId<'WorkflowInstanceId'>;
export const WorkflowInstanceId = zodBrandedUuid<WorkflowInstanceId>();
export type StepDefinitionId = BrandedId<'StepDefinitionId'>;
export const StepDefinitionId = zodBrandedString<StepDefinitionId>();
export type StepInstanceId = BrandedId<'StepInstanceId'>;
export const StepInstanceId = zodBrandedUuid<StepInstanceId>();
export type StepActivationId = BrandedId<'StepActivationId'>;
export const StepActivationId = zodBrandedUuid<StepActivationId>();
export type StepNoteId = BrandedId<'StepNoteId'>;
export type DelinquentInterestSettingId = BrandedId<'DelinquentInterestSettingId'>;
export const DelinquentInterestSettingId = zodBrandedUuid<DelinquentInterestSettingId>();
export type BannerMessageId = BrandedId<'BannerMessageId'>;
export const BannerMessageId = zodBrandedUuid<BannerMessageId>();
// Synthetic — minted by the exportable indexer; no table behind it (see ExportableBalanceHistoryItem).
export type BalanceHistoryItemId = BrandedId<'BalanceHistoryItemId'>;
export const BalanceHistoryItemId = zodBrandedString<BalanceHistoryItemId>();
export type BankruptcyId = BrandedId<'BankruptcyId'>;
export const BankruptcyId = zodBrandedUuid<BankruptcyId>();
export type BankruptcyProofOfClaimId = BrandedId<'BankruptcyProofOfClaimId'>;
export const BankruptcyProofOfClaimId = zodBrandedUuid<BankruptcyProofOfClaimId>();
export type NonPerformanceCodeForReportingCycleId = BrandedId<'NonPerformanceCodeForReportingCycleId'>;
export const NonPerformanceCodeForReportingCycleId = zodBrandedUuid<NonPerformanceCodeForReportingCycleId>();
export type ReportableEventId = BrandedId<'ReportableEventId'>;
export const ReportableEventId = zodBrandedUuid<ReportableEventId>();
export type ForeclosureId = BrandedId<'ForeclosureId'>;
export const ForeclosureId = zodBrandedUuid<ForeclosureId>();
export type QrpcId = BrandedId<'QrpcId'>;
export const QrpcId = zodBrandedUuid<QrpcId>();
export type PaymentBreakdownId = BrandedId<'PaymentBreakdownId'>;
export const PaymentBreakdownId = zodBrandedUuid<PaymentBreakdownId>();
export type TransactionId = BrandedId<'TransactionId'>;
export const TransactionId = zodBrandedString<TransactionId>();
export type LedgerPostingId = BrandedId<'LedgerPostingId'>;
export const LedgerPostingId = zodBrandedUuid<LedgerPostingId>();
export type LedgerTransactionId = BrandedId<'LedgerTransactionId'>;
export const LedgerTransactionId = zodBrandedUuid<LedgerTransactionId>();
export type LiquidationId = BrandedId<'LiquidationId'>;
export const LiquidationId = zodBrandedUuid<LiquidationId>();

/**
 * Simple utility for creating typesafe branded id values
 *
 * @param _brandType - the type of branded id to create
 * @param id - the id to use, defaults to a random v4 UUID
 */
export const brandedId = <T extends BrandedIdKey>(
  _brandType: T,
  id: string = globalThis.crypto.randomUUID(),
): BrandedId<T> => id as BrandedId<T>;
